/* eslint-disable react/jsx-no-target-blank */
import { TimelineMode } from '@src/editor';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { SidebarOption } from '@src/types.ts/general';
import { analytics } from '@src/utility/analytics';
import { Bold, BoldValues } from '@src/utility/fonts';
import { getMinMaxYPosition } from '@src/utility/videoTranscriptionUtils';
import { observer } from 'mobx-react-lite';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { KARAOKE_TRACK_NUMBER } from '../../stores/VideoCreatorStore';
import { numericalInputStyles } from '../../styles/mainStyle';
import CapitalizeIcon from '../../svgs/CapitalizationIcon';
import LeftAlignIcon from '../../svgs/LeftAlignIcon';
import MiddleAlignIcon from '../../svgs/MiddleAlignIcon';
import RightAlignIcon from '../../svgs/RightAlignIcon';
import {
  getFontStylePresets,
  isFontStylePlaceholderText,
} from '../../utility/fontStyles';
import {
  FONT_SIZE_DEFAULT,
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
} from '../../utility/general';
import { LogManager } from '../../utility/Logger';
import ColorDropdownSelect from '../common/ColorDropdownSelect';
import { TextIcon } from '../common/icons';
import KaraokeShadow from '../common/KaraokeShadow';
import { StepperNumberInput } from '../common/StepperNumberInput';
import TextBackgroundRadius from '../common/TextBackgroundRadius';
import TextBrandingTemplates from '../common/TextBrandingTemplates';
import SpinningLoading from '../SpinningLoading';
import AlignThresholdControl from './KaraokeSettings/AlignThresholdControl';
import BackgroundColorOpacityControl from './KaraokeSettings/BackgroundColorOpacityControl';
import KaraokeSaveOrCancelTemplate from './KaraokeSettings/KaraokeSaveOrCancelTemplate';
import { PropertyCaption } from './PropertyCaption';
import { PropertyNumericalInput } from './PropertyNumericalInput';
import { SaveNameModal } from './SaveNameModal';
import { TextAnimationSettings } from './TextAnimationSettings';
import TextFontPicker from './TextFontPicker';
import TextFontStylePicker from './TextFontStylePicker';
import { TextInput } from './TextInput';

import type { ElementState } from '../../renderer/ElementState';
import type { FontStylePreset } from '../../utility/fontStyles';
import type {
  LowerThirdTemplate,
  Template,
} from '../textProcessor/TextBrandingService';
import type { TextFontPickerTab } from './TextFontPicker';
import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

interface TextSettingsProps {
  activeElement: ElementState | null | undefined;
  hideActionButtons?: boolean;
  editorStore: EditorStoreLike;
}

enum Action {
  bold = 'Bold',
  italic = 'Italic',
  capitalize = 'Capitalize',
  align = 'Align',
  // bgColor = 'Background Color',
}

enum Align {
  leftAlign = 'Left Align',
  midAlign = 'Middle Align',
  rightAlign = 'Right Align',
}

export const AlignValues = {
  [Align.leftAlign]: '0%',
  [Align.midAlign]: '50%',
  [Align.rightAlign]: '100%',
};

enum Colors {
  black = 'black',
  white = 'white',
  red = 'red',
  green = 'green',
  orange = 'orange',
  blue = 'blue',
  yellow = 'yellow',
}

const formatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  maximumFractionDigits: 3,
  minimumFractionDigits: 0,
});

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
  /* No bottom spacing here: the panel card's Body already contributes
     --spacing-md, which is the whole of what the Speakers panel uses and is
     the pattern every tab now follows. Adding more here double-counted it. */
  position: relative;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable both-edges;
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
  box-sizing: border-box;
`;

const SIconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const SIconBtn = styled.button<{ isActive: boolean }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ isActive }) =>
    isActive ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'none'};
  border: ${({ isActive }) =>
    isActive ? '1px solid var(--accent)' : '1px solid transparent'};
  border-radius: 6px;
  cursor: pointer;
  transition:
    background 100ms ease,
    border-color 100ms ease;
  &:hover {
    background: ${({ isActive }) =>
      isActive
        ? 'color-mix(in srgb, var(--accent) 22%, transparent)'
        : 'var(--surface-elevated)'};
  }
  &:active {
    background: var(--border);
  }
`;

const SDivider = styled.div`
  width: 1px;
  height: 18px;
  background: var(--border);
  margin: 0 4px;
  flex-shrink: 0;
`;

const BackgroundShadowGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

const SControlCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TextTemplateGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.div``;

const PositionContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 10px;
`;

/**
 * "Add New Text Element" button.
 *
 * - `variant="primary"` (default): solid green primary CTA. Used when no
 *   text element is selected and creating a new one is the main available
 *   action.
 * - `variant="secondary"`: outline / surface style. Used when a text
 *   element is selected and the editing controls below should take visual
 *   priority. The button stays available but stops competing for attention.
 */
const AddTextButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: var(--btn-h-lg);
  padding: 0 var(--btn-px-lg);
  border-radius: var(--radius-button);
  font-size: var(--text-sm);
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    color 120ms ease;

  ${({ variant }) =>
    variant === 'secondary'
      ? css`
          background: transparent;
          color: var(--foreground);
          border: 1px solid var(--border);
          font-weight: var(--font-weight-medium);
          &:hover:not(:disabled) {
            background: var(--surface-elevated);
            border-color: var(--text-muted);
          }
          &:active:not(:disabled) {
            background: var(--border);
          }
        `
      : css`
          background: var(--primary);
          color: var(--primary-foreground);
          border: none;
          font-weight: var(--font-weight-semi-bold);
          &:hover:not(:disabled) {
            background: var(--primary-hover);
          }
          &:active:not(:disabled) {
            background: var(--primary-pressed);
          }
        `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TextWrapper = styled.div`
  width: 100%;
`;

const FieldName = styled.span`
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
`;

const logger = LogManager.getLogger('TextSettings');

export const TextSettings = observer(
  forwardRef<any, TextSettingsProps>((props, ref) => {
    const { editorStore } = props;
    const videoCreator = useVideoCreatorStore();
    const { source } = props.activeElement || {};
    const config = videoCreator.textProducer.getTextConfig();
    const template = videoCreator.textBrandingService
      .selectedBrandingTemplate as LowerThirdTemplate;
    const textWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (source && source.track !== KARAOKE_TRACK_NUMBER) {
        videoCreator.textProducer.setTextConfig(source);
      }
    }, [source, videoCreator.currentVideo?.aspectRatio]);

    useEffect(() => {
      if (!source) {
        videoCreator.textProducer.setTextConfig({
          ...config,
          time: videoCreator.time,
        });
      }
    }, [videoCreator.time, template]);

    useEffect(() => {
      if (videoCreator.textBrandingService.isLoading) {
        textWrapperRef.current?.scrollIntoView();
      }
    }, [videoCreator.textBrandingService.isLoading]);

    useEffect(
      () => () => {
        if (videoCreator.textBrandingService.isTemplateModified) {
          videoCreator.textBrandingService.isTemplateModified = false;
        }
      },
      [],
    );

    const [addNewTemplate, toggleAddNewTemplate] = useState<boolean>(false);
    const [templateName, setTemplateName] = useState<string>();
    const [templateNameError, setTemplateNameError] = useState<boolean>(false);
    const [allowCustomTemplateSave, setAllowCustomTemplateSave] =
      useState<boolean>(false);
    const [shouldUseTemplatePosition, setShouldUseTemplatePosition] =
      useState<boolean>(true);
    // Graphics → Text "Add New Template" naming modal. Replaces the old
    // inline name-input + Cancel / Save Caption Template flow on the
    // Template tab with a single modal (name + Save + Cancel).
    const [showAddTemplateModal, setShowAddTemplateModal] =
      useState<boolean>(false);
    // Track which TextFontPicker tab is active so we can hide the
    // "Save Caption Template" button while the user is browsing the
    // Template tab — saving from inside the template list itself is
    // confusing UX, since that *is* the template management surface.
    const [fontPickerTab, setFontPickerTab] =
      useState<TextFontPickerTab>('text');

    const hasStickyBottom =
      addNewTemplate ||
      allowCustomTemplateSave ||
      videoCreator.textBrandingService.isTemplateModified;

    const xAlignment = config?.x_alignment || '0%';
    const selectedFontFamily = config?.font_family;

    const getDefaultXAlign = () => {
      switch (xAlignment) {
        case '0%':
          return Align.leftAlign;
        case '50%':
          return Align.midAlign;
        case '100%':
          return Align.rightAlign;
        default:
          return Align.leftAlign;
      }
    };

    const alignType = getDefaultXAlign();
    const selectFillColor = config?.fill_color;
    const selectedBackgroundColor = config?.background_color;
    const selectedBackgroundAlignThreshold = config?.background_align_threshold;
    const selectedBackgroundBorderRadius = config?.background_border_radius;
    const selectedBackgroundOpacity = parseFloat(
      !selectedBackgroundColor || selectedBackgroundColor === 'transparent'
        ? '0'
        : (selectedBackgroundColor.split(',')[3]?.split(')')[0]?.trim() ?? '1'),
    );
    const selectedShadow = useMemo(
      () =>
        config.shadow_color && config.shadow_color !== 'false'
          ? {
              color: config.shadow_color as string,
              blur: config.shadow_blur as number,
              x: config.shadow_x as number,
              y: config.shadow_y as number,
            }
          : undefined,
      [config],
    );

    const getDefaultActions = () => {
      let actions: Action[] = [];
      if (config?.font_style === 'italic') {
        actions.push(Action.italic);
      } else {
        actions = actions.filter(action => action !== Action.italic);
      }

      if (config?.text_transform === 'uppercase') {
        actions.push(Action.capitalize);
      } else {
        actions = actions.filter(action => action !== Action.capitalize);
      }

      if (config?.font_weight && config?.font_weight !== '400') {
        actions.push(Action.bold);
      } else {
        actions = actions.filter(action => action !== Action.bold);
      }
      actions.push(Action.align);

      return actions;
    };

    const [selectedActions, setSelectedActions] =
      useState<Action[]>(getDefaultActions());

    useEffect(() => {
      setSelectedActions(getDefaultActions());
    }, [config]);

    const { font_size: fontSize } = config;
    // The element's real size in px (exact, renderer fallback for auto-sized
    // text), or the draft config's when no element is selected yet. Whole px,
    // the same rounding as the canvas toolbar's stepper, so the two fields
    // show one number and a step from either lands on the same value.
    const livePx = source
      ? videoCreator.textProducer.convertToPx(source.font_size, source.id)
      : null;
    const fontSizePx = Math.round(
      livePx ?? (Number(fontSize) || FONT_SIZE_DEFAULT),
    );

    const expandPropertyMap = (
      propertyMap: Record<string, any>,
    ): Record<string, any> => {
      if ('font_size' in propertyMap) {
        // eslint-disable-next-line no-param-reassign
        propertyMap.font_size = videoCreator.textProducer.convertToVh(
          propertyMap.font_size,
          props.activeElement?.source?.id,
        );
      }
      return propertyMap;
    };

    // Helper function to modify text element properties using headless editor or fallback to legacy
    const modifyProperty = (property: Record<string, any>) => {
      if (editorStore?.editor && props.activeElement?.source?.id) {
        // Use headless editor UpdateElementCommand
        const success = editorStore.editor.commandUpdateElement(
          props.activeElement.source.id,
          expandPropertyMap(property),
        );
        videoCreator.textProducer.modifyProperty(property, false, null); // null to prevent source update
        if (!success) {
          console.warn(`Failed to update text element properties:`, property);
        }
      } else {
        // Fallback to legacy method
        videoCreator.textProducer.modifyProperty(property);
      }
    };

    const isKaraokeText = props.activeElement?.track === KARAOKE_TRACK_NUMBER;

    // Manage template position checkbox state
    useEffect(() => {
      if (!source || isKaraokeText) {
        // For new elements or karaoke, default to checked
        setShouldUseTemplatePosition(true);
        return;
      }

      if (
        !template ||
        !videoCreator.textBrandingService.hasTemplatePositioning(template)
      ) {
        // No template or no positioning data
        setShouldUseTemplatePosition(false);
        return;
      }

      // Check if current element position matches template position
      const positionMatches =
        videoCreator.textBrandingService.doesElementMatchTemplatePosition(
          source,
          template,
        );
      setShouldUseTemplatePosition(positionMatches);
    }, [source, template, isKaraokeText, config?.x, config?.y]);

    const handleToggleAction = (
      action: Action,
      propertyName: keyof KaraokeConfig,
      normalState: string,
      requiredState: string,
    ) => {
      const isSelected = selectedActions.includes(action);

      const state = isSelected ? normalState : requiredState;
      modifyProperty({ [propertyName]: state });
    };

    const handleSetFont = (
      fontName: string,
      fontWeight?: string,
      fontStyle?: 'normal' | 'italic',
    ) => {
      modifyProperty({
        font_family: fontName,
        font_weight: fontWeight || BoldValues[Bold.normal].toString(),
        // Brand variants carry an explicit roman/italic style — apply it so
        // the element's font_style matches the registered font face (mirrors
        // TextFloatingToolbar's italic toggle). Without this, picking an
        // italic variant leaves font_style: 'normal' and Creatomate renders
        // the roman face. Omitted for popular fonts (synthetic italic toggled
        // separately) so we don't clobber their font_style.
        ...(fontStyle ? { font_style: fontStyle } : {}),
      });
    };

    // Graphics → Text "Add New Template" flow. Clicking the button opens a
    // naming modal; confirming saves the current text styling as a new
    // template and jumps to Captions where caption templates live.
    const openAddTemplateModal = () => {
      videoCreator.textBrandingService.prepareTemplateToSave = true;
      setShowAddTemplateModal(true);
    };

    const closeAddTemplateModal = () => {
      videoCreator.textBrandingService.prepareTemplateToSave = false;
      setShowAddTemplateModal(false);
    };

    const handleSaveTextTemplate = async (name: string) => {
      await videoCreator.textBrandingService.save(
        name,
        isKaraokeText ? 'karaoke' : 'basic',
        'text-settings',
        undefined,
        'create',
      );
      closeAddTemplateModal();
      videoCreator.sidebarOptions = SidebarOption.karaoke;
    };

    // Inline "Update" on the active template row — overwrites that template
    // with the current styling, keeping its existing name. No modal: the
    // template is already named and selected.
    const handleUpdateTemplate = async (template: Template) => {
      await videoCreator.textBrandingService.save(
        (template.title as string) || '',
        isKaraokeText ? 'karaoke' : 'basic',
        'text-settings',
        undefined,
        'update',
      );
    };

    /**
     * Apply a Font Style preset (Title / Subtitle / Text) by creating a
     * new text element pre-styled with the preset. Lets a creator skip
     * the "add empty text → manually configure" two-step.
     *
     * Only invoked from the no-selection state — the picker is hidden
     * once an element is selected (granular controls take over).
     *
     * `font_size` is passed as a numeric px string; `createTextElement`
     * runs it through `convertToVh` before the renderer call.
     */
    const handleApplyFontStyle = (preset: FontStylePreset) => {
      void createTextElement({
        font_family: preset.fontFamily,
        font_weight: preset.fontWeight.toString(),
        font_size: preset.fontSize.toString(),
        // Pre-fill the element with the preset's sample text so the
        // canvas mirrors the preview card the user clicked.
        text: preset.sample,
      });
    };

    /**
     * "Add New Text Element" button click: produce the same output as
     * clicking the Title preset card. Treats the Title preset as the
     * conventional default for ad-hoc new text — same placeholder
     * ("Insert a title"), same family / weight / size — so users get a
     * structured starting point instead of a tiny default-size empty
     * element. Falls back to the bare `createTextElement()` path only
     * if the Title preset can't be resolved (defensive — the defaults
     * always resolve, this is unreachable in practice).
     */
    const handleAddNewTextElement = () => {
      const titlePreset = getFontStylePresets(
        videoCreator.organization?.branding,
      ).find(p => p.key === 'title');
      if (titlePreset) {
        handleApplyFontStyle(titlePreset);
        return;
      }
      void createTextElement();
    };

    const renderFullTextSettings = () => (
      <>
        <TextFontPicker
          defaultValue={selectedFontFamily}
          fontWeight={config?.font_weight}
          onTabChange={setFontPickerTab}
          onSelectFont={handleSetFont}
          textContent={
            <>
              {!isKaraokeText && (
                <Item style={{ display: 'flex', flexDirection: 'column' }}>
                  <FieldName>Text</FieldName>
                  <TextInput
                    text={config?.text || ''}
                    placeholderText={
                      isFontStylePlaceholderText(config?.text)
                        ? config?.text
                        : undefined
                    }
                    modifyElement={text => {
                      if (text !== config?.text || '') {
                        modifyProperty({ text });
                      }
                    }}
                  />
                </Item>
              )}
              <Item>
                <FieldName>Color</FieldName>
                <ColorDropdownSelect
                  action={(propertyName, value) =>
                    modifyProperty({ [propertyName]: value })
                  }
                  propertyName="fill_color"
                  defaultValue={selectFillColor || Colors.black}
                />
              </Item>
              <Item>
                <FieldName>Font Size</FieldName>
                {/* Reads the element itself, like the canvas toolbar's
                 * stepper: `config.font_size` stands in 32 for auto-sized
                 * text, so the two showed different numbers for one
                 * element (PLAT-12088). A draft (no element yet) still
                 * shows the config value. Typed or stepped px goes through
                 * modifyProperty → expandPropertyMap, which writes vh. */}
                <StepperNumberInput
                  defaultValue={fontSizePx}
                  unit="px"
                  onChange={value => {
                    if (value === '') return;
                    modifyProperty({ font_size: String(value) });
                  }}
                  min={FONT_SIZE_MIN}
                  max={FONT_SIZE_MAX}
                  stepFromWhenEmpty={FONT_SIZE_DEFAULT}
                  decrementLabel="Decrease font size"
                  incrementLabel="Increase font size"
                />
              </Item>
              <SIconRow>
                <SIconBtn
                  isActive={selectedActions.includes(Action.capitalize)}
                  onClick={() =>
                    handleToggleAction(
                      Action.capitalize,
                      'text_transform',
                      'none',
                      'uppercase',
                    )
                  }
                  aria-label="Uppercase"
                  aria-pressed={selectedActions.includes(Action.capitalize)}
                >
                  <CapitalizeIcon
                    fillColor={
                      selectedActions.includes(Action.capitalize)
                        ? 'var(--foreground)'
                        : 'var(--text-muted)'
                    }
                  />
                </SIconBtn>
                <SDivider />
                <SIconBtn
                  isActive={alignType === Align.leftAlign}
                  onClick={() =>
                    modifyProperty({
                      x_alignment: AlignValues[Align.leftAlign],
                    })
                  }
                  aria-label="Align left"
                  aria-pressed={alignType === Align.leftAlign}
                >
                  <LeftAlignIcon
                    fillColor={
                      alignType === Align.leftAlign
                        ? 'var(--foreground)'
                        : 'var(--text-muted)'
                    }
                  />
                </SIconBtn>
                <SIconBtn
                  isActive={alignType === Align.midAlign}
                  onClick={() =>
                    modifyProperty({ x_alignment: AlignValues[Align.midAlign] })
                  }
                  aria-label="Align center"
                  aria-pressed={alignType === Align.midAlign}
                >
                  <MiddleAlignIcon
                    strokeColor={
                      alignType === Align.midAlign
                        ? 'var(--foreground)'
                        : 'var(--text-muted)'
                    }
                  />
                </SIconBtn>
                <SIconBtn
                  isActive={alignType === Align.rightAlign}
                  onClick={() =>
                    modifyProperty({
                      x_alignment: AlignValues[Align.rightAlign],
                    })
                  }
                  aria-label="Align right"
                  aria-pressed={alignType === Align.rightAlign}
                >
                  <RightAlignIcon
                    fillColor={
                      alignType === Align.rightAlign
                        ? 'var(--foreground)'
                        : 'var(--text-muted)'
                    }
                  />
                </SIconBtn>
              </SIconRow>
              <BackgroundShadowGroup>
                <SControlCol>
                  <FieldName>Background</FieldName>
                  <BackgroundColorOpacityControl
                    action={modifyProperty}
                    defaultValue={selectedBackgroundColor}
                    opacity={selectedBackgroundOpacity}
                  />
                </SControlCol>
                <SControlCol>
                  <FieldName>Fill</FieldName>
                  <AlignThresholdControl
                    backgroundAlignThreshold={selectedBackgroundAlignThreshold}
                    action={modifyProperty}
                  />
                </SControlCol>
                <SControlCol>
                  <FieldName>Radius</FieldName>
                  <TextBackgroundRadius
                    value={selectedBackgroundBorderRadius}
                    action={property => modifyProperty({ ...property })}
                  />
                </SControlCol>
                <SControlCol>
                  <FieldName>Shadow</FieldName>
                  <KaraokeShadow
                    type="basic"
                    action={property => modifyProperty({ ...property })}
                    value={selectedShadow || undefined}
                  />
                </SControlCol>
              </BackgroundShadowGroup>
            </>
          }
          animationContent={
            <>
              <TextAnimationSettings
                activeElement={props.activeElement}
                editorStore={editorStore}
              />
              <PositionContent>
                <Item>
                  <PropertyCaption>Start</PropertyCaption>
                  <PropertyNumericalInput
                    propertyName="time"
                    defaultValue={config.time || 0}
                    unit="s"
                    customStyles={numericalInputStyles}
                    getInputValue={async (property: string, value: string) => {
                      modifyProperty({ [property]: value });
                    }}
                  />
                </Item>
                <Item>
                  <PropertyCaption>End</PropertyCaption>
                  <PropertyNumericalInput
                    propertyName="time"
                    defaultValue={formatter.format(
                      (parseFloat(config.time) || 0) +
                        (parseFloat(config.duration) || 4),
                    )}
                    unit="s"
                    customStyles={numericalInputStyles}
                    getInputValue={async (property: string, value: string) => {
                      const duration = formatter.format(
                        parseFloat(value) - parseFloat(config.time) || 0,
                      );
                      modifyProperty({ duration });
                    }}
                  />
                </Item>
                <Item>
                  <PropertyCaption>Length</PropertyCaption>
                  <PropertyNumericalInput
                    propertyName="duration"
                    defaultValue={config.duration || 4}
                    unit="s"
                    customStyles={numericalInputStyles}
                    getInputValue={async (property: string, value: string) => {
                      modifyProperty({ [property]: value });
                    }}
                  />
                </Item>
              </PositionContent>
            </>
          }
          templateContent={
            <TextTemplateGroup>
              <TextBrandingTemplates
                ref={ref}
                hasStickyBottom={hasStickyBottom}
                addNewTemplate={addNewTemplate}
                toggleAddNewTemplate={toggleAddNewTemplate}
                setTemplateNameError={setTemplateNameError}
                templateNameError={templateNameError}
                setTemplateName={setTemplateName}
                hasActiveElement={
                  !!source && !videoCreator.textProducer.creatingNewText
                }
                type={isKaraokeText ? 'karaoke' : 'basic'}
                shouldUseTemplatePosition={shouldUseTemplatePosition}
                onAddNewClick={openAddTemplateModal}
                onUpdateTemplate={handleUpdateTemplate}
              />
            </TextTemplateGroup>
          }
        />
        {!props.hideActionButtons && fontPickerTab !== 'template' && (
          // Hidden on the Template tab: that tab manages templates and now
          // owns its own "+ Add New Template" → naming-modal flow (see
          // openAddTemplateModal). The button stays on Text / Font /
          // Animation tabs as the natural "capture current settings" spot.
          <KaraokeSaveOrCancelTemplate
            addNewTemplate={addNewTemplate}
            setTemplateName={setTemplateName}
            setTemplateNameError={setTemplateNameError}
            templateName={templateName}
            templateNameError={templateNameError}
            toggleAddNewTemplate={toggleAddNewTemplate}
            allowCustomTemplateSave={allowCustomTemplateSave}
            setAllowCustomTemplateSave={setAllowCustomTemplateSave}
            type={isKaraokeText ? 'karaoke' : 'basic'}
            setting="text-settings"
            // The Graphics → Text surface exposes Save only — the legacy
            // "Update Template" concept is being phased out here. See
            // PLAT-9973 / PLAT-9974.
            hideUpdateButton
            // After a successful save, jump the user to the Captions tab
            // so the freshly-saved caption template is immediately
            // discoverable on its natural surface.
            onAfterSave={() => {
              videoCreator.sidebarOptions = SidebarOption.karaoke;
            }}
          />
        )}
        {showAddTemplateModal && (
          <SaveNameModal
            headingText="Save Text Template"
            closeModal={closeAddTemplateModal}
            onConfirm={handleSaveTextTemplate}
            onCancel={closeAddTemplateModal}
            confirmText="Save"
            cancelText="Cancel"
            initialName=""
          />
        )}
      </>
    );

    // Helper function to create text element using headless editor or fallback to legacy
    /**
     * Create a new text element on the canvas.
     *
     * When `overrides` is provided, the corresponding properties replace
     * the producer defaults — used by the Font Style picker to spawn a
     * text element already styled as Title / Subtitle / Text *and*
     * pre-filled with the preset's sample text (so the canvas shows
     * exactly what the preview promised). Passing `undefined` (the bare
     * "Add New Text Element" button path) preserves the original
     * behaviour.
     */
    const createTextElement = async (
      overrides?: Partial<{
        font_family: string;
        font_weight: string;
        /** px-as-string; converted to vh inside this function. */
        font_size: string;
        /** Pre-fills the element's text content. */
        text: string;
      }>,
    ) => {
      // Track analytics like the original method
      analytics.track('create_text_element', {
        storyId: videoCreator.story?.id,
        storyTitle: videoCreator.story?.title,
        videoId: videoCreator.currentVideo?.id,
        videoTitle: videoCreator.currentVideo?.title,
      });

      // Set creatingNewText flag to prevent template switching during creation
      videoCreator.textProducer.creatingNewText = true;

      // Get text producer config for smart positioning and aspect ratio defaults
      const textConfig = videoCreator.textProducer.initializeConfig();
      const currentConfig = videoCreator.textProducer.getTextConfig();

      // Smart positioning logic (from original produceText)
      let targetY = textConfig.y;
      const activeElement = videoCreator.getActiveElement();
      const activeSource = activeElement?.source;

      if (
        activeSource?.type === 'text' &&
        activeSource.track !== KARAOKE_TRACK_NUMBER
      ) {
        const height = Math.ceil(parseFloat(activeSource.font_size) * 1.7);
        targetY = `${parseFloat(activeSource.y || '50') + height}%`;
        const minMaxYPos = getMinMaxYPosition(
          videoCreator.rootStore.videoSourceStore.source?.height,
          videoCreator.textProducer
            .getFontSize(currentConfig.font_size)
            .toString(),
        );
        const max = 100 - minMaxYPos[0];
        if (parseFloat(targetY) > max) {
          targetY = textConfig.y;
        }
      }

      // Apply template positioning if available, otherwise use default positioning
      let finalX = '50%';
      let finalY = targetY;

      if (
        !isKaraokeText &&
        template &&
        videoCreator.textBrandingService.hasTemplatePositioning(template)
      ) {
        if (template.xPos !== undefined && template.xPos !== null) {
          finalX = videoCreator.textBrandingService.formatPositionValue(
            template.xPos,
          );
        }
        if (template.yPos !== undefined && template.yPos !== null) {
          finalY = videoCreator.textBrandingService.formatPositionValue(
            template.yPos,
          );
        }
      }

      // Prepare properties for the text element
      const {
        /* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
        id: _id,
        track: _track,
        time: _time,
        /* eslint-enable @typescript-eslint/no-unused-vars, no-unused-vars */
        transcript_effect,
        transcript_split,
        transcript_placement,
        transcript_maximum_length,
        line_break_style,
        transcript_color,
        transcript_source,
        ...config
      } = currentConfig;
      const textProps = {
        ...textConfig,
        ...config,
        // Preset-provided text wins over the producer default so a
        // "Title"-styled element appears with "Insert a title" as its
        // initial content — matching the preview the user just clicked.
        text: overrides?.text ?? textConfig.text ?? '',
        x: finalX,
        y: finalY,
        font_size: videoCreator.textProducer.convertToVh(
          // Font Style preset overrides win over the producer default
          // so a "Title"-styled element is created at 48 px, etc.
          overrides?.font_size ?? currentConfig.font_size ?? '32',
        ),
        time: videoCreator.time,
        ...(overrides?.font_family
          ? { font_family: overrides.font_family }
          : {}),
        ...(overrides?.font_weight
          ? { font_weight: overrides.font_weight }
          : {}),
      };

      if (
        transcript_effect ||
        transcript_split ||
        transcript_placement ||
        transcript_maximum_length ||
        line_break_style ||
        transcript_color ||
        transcript_source
      ) {
        console.warn('Karaoke-specific fields found at createTextElement', {
          ...config,
        });
      }

      // Create element using headless editor command
      const element = editorStore.editor.commandCreateTextElement(
        textProps,
        undefined,
        TimelineMode.DEFAULT, // Use default timeline mode, ripple mode is not enabled yet
      );

      if (element) {
        // Handle template association like original method
        const templateId = videoCreator.textBrandingService.selectedTemplateId;

        if (templateId) {
          if (videoCreator.currentVideo) {
            const existingData =
              videoCreator.currentVideo.extraElementData || {};
            videoCreator.currentVideo.extraElementData = {
              ...existingData,
              lowerThirdTemplates: {
                ...(existingData?.lowerThirdTemplates || {}),
                [element.id]: templateId,
              },
            };
          } else {
            logger.error(
              'Cannot attach lower-third template id to text element: currentVideo not set',
            );
          }
        }

        // Auto-select element after delay
        setTimeout(async () => {
          console.log(
            `[TextSettings] Auto-selecting newly created text element: ${element.id}`,
          );
          await videoCreator.setActiveElements(element.id);
          videoCreator.textProducer.creatingNewText = false;
        }, 300);
      }
    };

    return (
      <Main>
        {videoCreator.textBrandingService.isLoading && (
          <SpinningLoading
            text={
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>Saving template…</span>
              </div>
            }
          />
        )}
        {!props.hideActionButtons && (
          <>
            <TextWrapper ref={textWrapperRef}>
              <AddTextButton
                type="button"
                onClick={handleAddNewTextElement}
                variant={props.activeElement ? 'secondary' : 'primary'}
              >
                {/* `currentColor` lets the icon follow the button's text
                 * colour — primary-foreground in the green CTA state,
                 * foreground in the secondary outlined state. */}
                <TextIcon height="20" width="20" fillColor="currentColor" />
                Add New Text Element
              </AddTextButton>
            </TextWrapper>
            {/*
             * Font Style picker only renders when no text element is
             * selected — it's a "pick a hierarchy, get a new pre-styled
             * element" shortcut. Once an element is selected, the
             * granular controls below take over and the preset cards
             * would compete with them visually.
             */}
            {!props.activeElement && (
              <TextFontStylePicker onSelect={handleApplyFontStyle} />
            )}
          </>
        )}

        {props.activeElement && renderFullTextSettings()}
      </Main>
    );
  }),
);
